/**
 * Output/input shapes used by the IPC command registry that are not stored records
 * (those live in providers/records.ts) and not server entities (api/entities.ts).
 */
import { z } from 'zod';
import {
  AccountIdSchema,
  AgendaIdSchema,
  ArtifactIdSchema,
  CanonicalKeySchema,
  ConnectionDraftIdSchema,
  ConnectionIdSchema,
  DatasetIdSchema,
  DeviceIdSchema,
  // notifications change
  NotificationIdSchema,
  ObjectTypeSchema,
  OperationIdSchema,
  OutreachBatchIdSchema,
  ProviderSchema,
  RecordKeySchema,
  RunIdSchema,
  SnapshotIdSchema,
  TaskGroupIdSchema,
  TemplateIdSchema,
  TenantIdSchema,
  WorkspaceIdSchema,
} from '../ids.js';
import { CapabilitySchema } from '../roles.js';
import {
  CompletenessStateSchema,
  ConnectionHealthSchema,
  DatasetRunStateSchema,
  EmploymentStateSchema,
  EntitlementStateSchema,
  SeverityLevelSchema,
  StandardStatusSchema,
  SyncErrorCodeSchema,
  SyncJobStateSchema,
  TriSchema,
} from '../status.js';
import {
  AccountSchema,
  CapabilityManifestEntrySchema,
  ConnectionSchema,
  ContentManifestSchema,
  // notifications change
  NotificationSchema,
  ReleaseChannelSchema,
  SessionUserSchema,
  TemplateTypeSchema,
} from '../api/entities.js';
import { DiffHunkSchema } from '../api/admin/templates.js';
import { Ed25519JwkSchema, IsoDateTimeSchema, MicroUsdSchema, SemVerSchema } from '../api/common.js';
import {
  ArtifactSchema,
  ClassificationSchema,
  DisplayPrefsSchema,
  OutreachContactsSchema,
  EffectiveAskSchema,
  HttpMethodSchema,
  LinkSchema,
  NoteSchema,
  OverrideSchema,
  RecordTimestampsSchema,
  RuleSchema,
  SnapshotSchema,
  SourceRecordSchema,
  SyncRunSchema,
  TaskGroupCategorySchema,
  UsageVerificationStatusSchema,
} from '../providers/records.js';

const count = z.number().int().nonnegative();

// ---------------------------------------------------------------------------------------------
// System / session state
// ---------------------------------------------------------------------------------------------

// 'recovery' added by desktop-main fix pass (LH-03/F05): the profile database refused to open
// (integrity check or migration failed, A21); a recovery screen replaces the workspace.
export const APP_PHASES = ['starting', 'signed_out', 'signing_in', 'ready', 'locked', 'update_required', 'offline', 'recovery'] as const;
export const AppPhaseSchema = z.enum(APP_PHASES);
export type AppPhase = z.infer<typeof AppPhaseSchema>;

export const LOCK_REASONS = ['entitlement_expired', 'server_denied', 'clock_rollback', 'verification_failed', 'manual', 'sleep_wake'] as const;
export const LockReasonSchema = z.enum(LOCK_REASONS);
export type LockReason = z.infer<typeof LockReasonSchema>;

export const EntitlementSummarySchema = z.object({
  state: EntitlementStateSchema,
  expiresAt: IsoDateTimeSchema.nullable(),
  remainingSeconds: count.nullable(),
  issuedAt: IsoDateTimeSchema.nullable(),
  policyVersion: z.number().int().positive().nullable(),
  minAppVersion: SemVerSchema.nullable(),
  accountIds: z.array(AccountIdSchema),
  capabilities: z.array(CapabilitySchema),
  rumiDisabledAccountIds: z.array(AccountIdSchema),
  lastRenewalAttemptAt: IsoDateTimeSchema.nullable(),
  lastRenewalError: z.string().nullable(),
});
export type EntitlementSummary = z.infer<typeof EntitlementSummarySchema>;

export const StorageStatusSchema = z.object({
  usedBytes: count,
  budgetBytes: count,
  warn: z.boolean(),
  warnRatio: z.number(),
  freeDiskBytes: count.nullable(),
  pausedForDisk: z.boolean(),
  breakdown: z.array(z.object({ category: z.enum(['profile', 'accounts', 'staging', 'bundles', 'diagnostics', 'backups', 'other']), bytes: count })),
  measuredAt: IsoDateTimeSchema,
});
export type StorageStatus = z.infer<typeof StorageStatusSchema>;

export const GoogleStatusSchema = z.object({
  connected: z.boolean(),
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  sendAs: z.array(z.object({ email: z.string(), displayName: z.string().nullable(), isDefault: z.boolean() })),
  scopes: z.array(z.string()),
  expiresAt: IsoDateTimeSchema.nullable(),
  needsReauth: z.boolean(),
  lastError: z.string().nullable(),
  connectedAt: IsoDateTimeSchema.nullable(),
  // added by desktop-main fix pass (F07): required scopes the stored grant lacks (A20); when
  // non-empty, needsReauth is true and lastError is 'scopes_changed'.
  missingScopes: z.array(z.string()).optional(),
  // real-google change
  /** False until an administrator stores the desktop OAuth client; Settings -> Google disables Connect. */
  configured: z.boolean(),
  /** True when the connected Google identity differs from the signed-in Agency user (warning line; switching is allowed). */
  identityMismatch: z.boolean(),
  /** Hosted domain preselected on consent (`hd`); null when the administrator has not set one. */
  hostedDomain: z.string().nullable(),
  /** `sim` only under AGENCY_GOOGLE_SIM=1 (Playwright); never by default. */
  mode: z.enum(['google', 'sim']),
});
export type GoogleStatus = z.infer<typeof GoogleStatusSchema>;

export const ContentStateSchema = z.object({
  activeVersion: SemVerSchema.nullable(),
  activeReleaseId: z.string().nullable(),
  channel: ReleaseChannelSchema,
  activatedAt: IsoDateTimeSchema.nullable(),
  lastCheckedAt: IsoDateTimeSchema.nullable(),
  updateAvailable: SemVerSchema.nullable(),
  lastKnownGoodVersion: SemVerSchema.nullable(),
  lastError: z.string().nullable(),
  itemCount: count,
});
export type ContentState = z.infer<typeof ContentStateSchema>;

// added by desktop-main fix pass (LH-03/F05): profile database recovery state (A21, A27). Mirrors
// the worker's RecoveryState sidecar; paths are local file paths, never secrets.
export const RecoveryReasonSchema = z.enum(['integrity_check_failed', 'migration_failed', 'open_failed', 'backup_failed']);
export type RecoveryReason = z.infer<typeof RecoveryReasonSchema>;
export const RecoveryStateSchema = z.object({
  reason: RecoveryReasonSchema,
  message: z.string(),
  filePath: z.string().nullable().optional(),
  backupPath: z.string().nullable(),
  failedMigration: z.string().nullable().optional(),
  at: IsoDateTimeSchema,
});
export type RecoveryState = z.infer<typeof RecoveryStateSchema>;

export const AppStateSchema = z.object({
  phase: AppPhaseSchema,
  appVersion: SemVerSchema,
  mode: z.enum(['local', 'cloud']),
  online: z.boolean(),
  workerReady: z.boolean(),
  user: SessionUserSchema.nullable(),
  deviceId: DeviceIdSchema.nullable(),
  entitlement: EntitlementSummarySchema,
  content: ContentStateSchema,
  storage: StorageStatusSchema,
  google: GoogleStatusSchema,
  lock: z.object({ reason: LockReasonSchema, at: IsoDateTimeSchema, remainingSeconds: count.nullable() }).nullable(),
  serverBaseUrl: z.string().url(),
  dataDir: z.string(),
  // added by desktop-main fix pass (LH-03/F05): non-null while phase is 'recovery'.
  recovery: RecoveryStateSchema.nullable().optional(),
  // added by desktop-main usability pass: development-only provider rate multiplier
  // (AGENCY_DEV_RATE_SCALE, paired with the sim's SIM_RATE_LIMIT_SCALE). null/absent means the
  // published ceilings apply; the renderer shows a "LOCAL · RATE ×N" chip when it is > 1.
  devRateScale: z.number().min(1).max(100).nullable().optional(),
  // real-providers change: true only while the desktop points Vanta/Drata at the simulator
  // (AGENCY_PROVIDER_SIM=1, Playwright); Settings -> Developer shows "Provider pacing" only then.
  providerSimActive: z.boolean().optional(),
});
export type AppState = z.infer<typeof AppStateSchema>;

export const DeviceInfoSchema = z.object({
  deviceId: DeviceIdSchema.nullable(),
  name: z.string(),
  platform: z.enum(['darwin-arm64', 'darwin-x64']),
  osVersion: z.string(),
  appVersion: SemVerSchema,
  electronVersion: z.string(),
  publicKeyJwk: Ed25519JwkSchema.nullable(),
  registeredAt: IsoDateTimeSchema.nullable(),
  status: z.enum(['unregistered', 'active', 'revoked', 'update_required']),
  dataDir: z.string(),
  bundleId: z.string(),
});
export type DeviceInfo = z.infer<typeof DeviceInfoSchema>;

export const DocPageSchema = z.object({
  slug: z.string(),
  title: z.string(),
  section: z.enum(['setup', 'workflows', 'field_mappings', 'shortcuts', 'storage_retention', 'errors', 'release_notes', 'support']),
  /** Restricted Markdown from the content bundle. */
  contentMarkdown: z.string(),
  contentVersion: SemVerSchema.nullable(),
  order: count,
});
export type DocPage = z.infer<typeof DocPageSchema>;

export const UpdateCheckResultSchema = z.object({
  status: z.enum(['idle', 'checking', 'downloading', 'downloaded', 'installing', 'up_to_date', 'available', 'not_approved', 'disabled', 'error', 'blocked_by_write']),
  currentVersion: SemVerSchema,
  availableVersion: SemVerSchema.nullable(),
  approvedBuildId: z.string().nullable(),
  downloadPercent: z.number().min(0).max(100).nullable().default(null),
  checkedAt: IsoDateTimeSchema,
  /** e.g. "Up to date · 1.0.0-local" */
  message: z.string(),
});
export type UpdateCheckResult = z.infer<typeof UpdateCheckResultSchema>;

export const SupportBundleOptionsSchema = z.object({
  includeDiagnostics: z.boolean().default(true),
  includeIssues: z.boolean().default(true),
  includeAuditOutboxStatus: z.boolean().default(true),
  includeLayout: z.boolean().default(false),
  redactAccountNames: z.boolean().default(true),
  accountIds: z.array(AccountIdSchema).optional(),
  fromDate: IsoDateTimeSchema.optional(),
});
export type SupportBundleOptions = z.infer<typeof SupportBundleOptionsSchema>;

export const SupportBundleManifestSchema = z.object({
  generatedAt: IsoDateTimeSchema,
  appVersion: SemVerSchema,
  deviceId: DeviceIdSchema.nullable(),
  entries: z.array(z.object({ name: z.string(), bytes: count, lineCount: count, redacted: z.boolean(), sampleLines: z.array(z.string()).max(5) })),
  redactionReport: z.object({ secretsRemoved: count, emailsRemoved: count, urlsRemoved: count, accountNamesRemoved: count }),
  /** Never included: tokens, provider bodies, prompts, recipient lists. */
  excluded: z.array(z.string()),
});
export type SupportBundleManifest = z.infer<typeof SupportBundleManifestSchema>;

// ---------------------------------------------------------------------------------------------
// Local account and connection views
// ---------------------------------------------------------------------------------------------

export const SnapshotSummarySchema = SnapshotSchema.pick({
  id: true,
  connectionId: true,
  committedAt: true,
  complete: true,
  status: true,
  stale: true,
  recordCount: true,
  isCurrent: true,
}).extend({ coverageState: CompletenessStateSchema });
export type SnapshotSummary = z.infer<typeof SnapshotSummarySchema>;

export const ConnectionSummarySchema = z.object({
  id: ConnectionIdSchema,
  provider: ProviderSchema,
  workspaceId: WorkspaceIdSchema.nullable(),
  tenantLabel: z.string().nullable(),
  health: ConnectionHealthSchema,
  lastSyncAt: IsoDateTimeSchema.nullable(),
  lastSyncState: SyncJobStateSchema.nullable(),
  activeRunId: RunIdSchema.nullable(),
});
export type ConnectionSummary = z.infer<typeof ConnectionSummarySchema>;

/** Account as the renderer sees it: server fields plus local state. */
export const LocalAccountSchema = AccountSchema.extend({
  displayPrefs: DisplayPrefsSchema,
  connections: z.array(ConnectionSummarySchema),
  currentSnapshot: SnapshotSummarySchema.nullable(),
  selectedSnapshotId: SnapshotIdSchema.nullable(),
  lastRun: z.object({ id: RunIdSchema, state: SyncJobStateSchema, endedAt: IsoDateTimeSchema.nullable(), statusText: z.string() }).nullable(),
  coverageState: CompletenessStateSchema,
  hasLocalData: z.boolean(),
  pendingAskCount: count,
  failingCount: count,
  artifactCount: count,
  localNotesRevision: count.nullable(),
  assigned: z.boolean(),
  rumiDisabled: z.boolean(),
  /** Customer contacts the analyst may Cc on outreach (template feedback 2026-09-25). */
  outreachContacts: OutreachContactsSchema,
});
export type LocalAccount = z.infer<typeof LocalAccountSchema>;

export const LocalConnectionSchema = ConnectionSchema.extend({
  credentialPresent: z.boolean(),
  apiBaseUrl: z.string().url(),
  /** Vanta: tenant browser URL, kept separate from the API base URL. */
  browserUrl: z.string().url().nullable(),
  tenantId: TenantIdSchema.nullable(),
  tokenExpiresAt: IsoDateTimeSchema.nullable(),
  lastTestedAt: IsoDateTimeSchema.nullable(),
  lastTestResult: z.enum(['ok', 'failed', 'never']),
  activeRunId: RunIdSchema.nullable(),
  currentSnapshotId: SnapshotIdSchema.nullable(),
});
export type LocalConnection = z.infer<typeof LocalConnectionSchema>;

export const DiscoveryResultSchema = z.object({
  draftId: ConnectionDraftIdSchema,
  provider: ProviderSchema,
  tenant: z.object({
    tenantId: TenantIdSchema,
    displayName: z.string().nullable(),
    browserUrl: z.string().url().nullable(),
  }),
  capabilities: z.array(CapabilityManifestEntrySchema),
  adapterVersion: z.string(),
  workspaces: z.array(z.object({ id: WorkspaceIdSchema, name: z.string(), isDefault: z.boolean() })),
  workspaceSelectionRequired: z.boolean(),
  warnings: z.array(z.string()),
  /** Draft credentials are discarded after this time unless attached. */
  expiresAt: IsoDateTimeSchema,
});
export type DiscoveryResult = z.infer<typeof DiscoveryResultSchema>;

export const AttachResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('attached'), connection: LocalConnectionSchema }),
  z.object({
    status: z.literal('tenant_mismatch'),
    expectedTenantId: TenantIdSchema.nullable(),
    discoveredTenantId: TenantIdSchema,
    /** Accounts already bound to the discovered tenant, if any. */
    matchingAccountIds: z.array(AccountIdSchema),
  }),
  z.object({ status: z.literal('workspace_required'), workspaces: z.array(z.object({ id: WorkspaceIdSchema, name: z.string(), isDefault: z.boolean() })) }),
]);
export type AttachResult = z.infer<typeof AttachResultSchema>;

export const ConnectionTestResultSchema = z.object({
  ok: z.boolean(),
  health: ConnectionHealthSchema,
  checks: z.array(z.object({ name: z.enum(['credentials', 'tenant_identity', 'scopes', 'lease', 'datasets', 'workspace']), status: z.enum(['ok', 'failed', 'skipped', 'warning']), message: z.string() })),
  capabilities: z.array(CapabilityManifestEntrySchema),
  testedAt: IsoDateTimeSchema,
  errorCode: SyncErrorCodeSchema.nullable(),
});
export type ConnectionTestResult = z.infer<typeof ConnectionTestResultSchema>;

export const TokenDiagnosticsSchema = z.object({
  provider: ProviderSchema,
  health: ConnectionHealthSchema,
  issuedAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
  scopes: z.array(z.string()),
  leaseHeld: z.boolean(),
  /** Only when reveal=true, credential.debug held and local reauthentication succeeded. */
  tokenValue: z.string().optional(),
  revealedAt: IsoDateTimeSchema.nullable(),
});
export type TokenDiagnostics = z.infer<typeof TokenDiagnosticsSchema>;

// ---------------------------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------------------------

export const ScopePreviewSchema = z.object({
  connectionId: ConnectionIdSchema,
  provider: ProviderSchema,
  datasets: z.array(z.object({ id: DatasetIdSchema, label: z.string(), supported: z.boolean(), reason: z.string().nullable(), estimatedRequests: count, dependsOn: z.array(DatasetIdSchema), lastCount: count.nullable() })),
  estimatedRequests: count,
  estimatedDurationSeconds: count,
  rateLimitCeiling: z.number().int().positive(),
  warnings: z.array(z.string()),
  preflight: z.array(z.object({ check: z.enum(['entitlement', 'connection_identity', 'credentials', 'scopes', 'adapter_version', 'free_disk', 'requested_datasets', 'lease', 'active_job']), status: z.enum(['ok', 'failed', 'warning']), message: z.string() })),
  canStart: z.boolean(),
  previousSnapshotId: SnapshotIdSchema.nullable(),
});
export type ScopePreview = z.infer<typeof ScopePreviewSchema>;

// ---------------------------------------------------------------------------------------------
// Product analytics (PostHog, GET-13): renderer-originated events forwarded by main
// ---------------------------------------------------------------------------------------------

/** Event names the renderer may report; main forwards them to PostHog with allowlisted primitive props. */
export const TELEMETRY_UI_EVENTS = ['view_opened', 'sync_requested'] as const;
export const TelemetryUiEventSchema = z.enum(TELEMETRY_UI_EVENTS);
export type TelemetryUiEvent = z.infer<typeof TelemetryUiEventSchema>;
/** Short primitive properties only (view ids, counts, flags); never record text or addresses. */
export const TelemetryPropsSchema = z.record(z.string().max(40), z.union([z.string().max(120), z.number(), z.boolean()]));
export type TelemetryProps = z.infer<typeof TelemetryPropsSchema>;

export const DatasetProgressSchema = z.object({
  datasetId: DatasetIdSchema,
  state: DatasetRunStateSchema,
  pageCount: count,
  fetchedCount: count,
  providerTotal: count.nullable(),
  requestCount: count,
  retryCount: count,
});
export type DatasetProgress = z.infer<typeof DatasetProgressSchema>;

// ---------------------------------------------------------------------------------------------
// Sources
// ---------------------------------------------------------------------------------------------

// `tests_documents` and `controls` are the Sources tabs; `tests` (tests + controls + monitors) and
// `documents` stay accepted for existing callers and exports.
export const SOURCE_VIEWS = ['all', 'tests_documents', 'controls', 'tests', 'documents', 'included', 'policies', 'infrastructure', 'company_compliance', 'employee_compliance'] as const;
export const SourceViewSchema = z.enum(SOURCE_VIEWS);
export type SourceView = z.infer<typeof SourceViewSchema>;

export const SOURCE_SORT_FIELDS = ['title', 'status', 'severity', 'objectType', 'sourceUpdatedAt', 'childCount', 'framework'] as const;
export const SourceSortSchema = z.object({ field: z.enum(SOURCE_SORT_FIELDS), direction: z.enum(['asc', 'desc']) });

export const SourceQuerySchema = z.object({
  accountId: AccountIdSchema,
  snapshotId: SnapshotIdSchema.nullable().optional(),
  connectionId: ConnectionIdSchema.optional(),
  view: SourceViewSchema.default('all'),
  search: z.string().max(200).default(''),
  showOk: z.boolean().default(false),
  showDeactivated: z.boolean().default(false),
  showIrrelevant: z.boolean().default(false),
  frameworkIds: z.array(z.string()).default([]),
  drataInScopeOnly: z.boolean().default(true),
  missingAskOnly: z.boolean().default(false),
  objectTypes: z.array(ObjectTypeSchema).optional(),
  statuses: z.array(StandardStatusSchema).optional(),
  sort: SourceSortSchema.default({ field: 'title', direction: 'asc' }),
  page: count.default(0),
  pageSize: z.number().int().min(10).max(500).default(100),
});
export type SourceQuery = z.infer<typeof SourceQuerySchema>;

export const EffectiveClassificationSchema = ClassificationSchema.extend({
  customAskNeeded: z.boolean(),
  /** Which layer supplied each flag. */
  provenance: z.enum(['account_override', 'library_override', 'published_rule', 'provider', 'default']),
});

export const SourceRowSchema = SourceRecordSchema.omit({ raw: true, rawDigest: true }).extend({
  /** Connection that collected the record (added by desktop-worker fix pass, LH-07). */
  connectionId: ConnectionIdSchema.nullable().optional(),
  included: TriSchema,
  irrelevant: z.boolean(),
  effectiveClassification: EffectiveClassificationSchema,
  effectiveAsk: EffectiveAskSchema,
  hasOverride: z.boolean(),
  parentTitle: z.string().nullable(),
  /** When a filtered child matches, the parent row is kept with this count (spec section 5). */
  matchedChildCount: count.nullable(),
  failingChildCount: count,
});
export type SourceRow = z.infer<typeof SourceRowSchema>;

export const SourceCountsSchema = z.object({ ok: count, failing: count, deactivated: count, irrelevant: count, missingAsk: count, total: count });

export const SourceQueryResultSchema = z.object({
  rows: z.array(SourceRowSchema),
  total: count,
  page: count,
  pageSize: count,
  counts: SourceCountsSchema,
  snapshotId: SnapshotIdSchema.nullable(),
  snapshotAt: IsoDateTimeSchema.nullable(),
  coverageState: CompletenessStateSchema,
  frameworks: z.array(z.object({ id: z.string(), name: z.string(), count: count })),
  /** Every framework of the snapshot, sorted by name: the numbering the Sources chips and the Frameworks filter share, independent of the current filters. */
  snapshotFrameworks: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type SourceQueryResult = z.infer<typeof SourceQueryResultSchema>;

export const SourceRecordDetailSchema = z.object({
  record: SourceRecordSchema,
  row: SourceRowSchema,
  accountOverride: OverrideSchema.nullable(),
  libraryOverride: OverrideSchema.nullable(),
  publishedRule: RuleSchema.nullable(),
  effectiveAsk: EffectiveAskSchema,
  effectiveClassification: EffectiveClassificationSchema,
  parent: SourceRowSchema.nullable(),
  children: z.object({ total: count, failing: count, preview: z.array(SourceRowSchema).max(20) }),
  links: z.array(LinkSchema),
  timestamps: RecordTimestampsSchema,
  frameworks: z.array(z.object({ id: z.string(), name: z.string() })),
  /** Field-level history across retained snapshots (most recent first). */
  history: z.array(z.object({ snapshotId: SnapshotIdSchema, committedAt: IsoDateTimeSchema, status: StandardStatusSchema, rawStatus: z.string().nullable() })),
  usedByRuleAccounts: count,
});
export type SourceRecordDetail = z.infer<typeof SourceRecordDetailSchema>;

export const ExportManifestSchema = z.object({
  accountId: AccountIdSchema,
  accountName: z.string(),
  snapshotId: SnapshotIdSchema.nullable(),
  snapshotAt: IsoDateTimeSchema.nullable(),
  filters: SourceQuerySchema.omit({ page: true, pageSize: true }),
  schemaVersion: z.number().int().positive(),
  recordCount: count,
  generatedAt: IsoDateTimeSchema,
  appVersion: SemVerSchema,
  contentVersion: SemVerSchema.nullable(),
  coverageState: CompletenessStateSchema,
  digest: z.string(),
});
export type ExportManifest = z.infer<typeof ExportManifestSchema>;

export const MissingAskQueueSchema = z.object({
  rows: z.array(SourceRowSchema),
  total: count,
  resolved: count,
  remaining: count,
  page: count,
});

// ---------------------------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------------------------

export const RuleViewSchema = RuleSchema.extend({
  libraryOverride: OverrideSchema.nullable(),
  effectiveRemediationText: z.string().nullable(),
  effectiveClassification: ClassificationSchema,
  usageAccountCount: count,
  usageRecordCount: count,
  /**
   * The provider's own default text for this rule, taken from synced records on this device:
   * Vanta test remediation when present, else the test/control description. It is what an analyst
   * rewrites into the ask (by hand or with rules.suggestAsk). Null when no local record carries text.
   */
  providerDefaultText: z.string().nullable().optional(),
  providerDefaultSource: z.enum(['remediation', 'description']).nullable().optional(),
});
export type RuleView = z.infer<typeof RuleViewSchema>;

export const RulePatchSchema = z.object({
  displayDescription: z.string().max(20_000).nullable().optional(),
  remediationText: z.string().max(20_000).nullable().optional(),
  classification: ClassificationSchema.partial().optional(),
  customAskNeeded: z.boolean().nullable().optional(),
});

export const RuleUsageSchema = z.object({
  canonicalKey: CanonicalKeySchema,
  accounts: z.array(z.object({ accountId: AccountIdSchema, displayName: z.string(), recordKeys: z.array(RecordKeySchema), count: count })),
});

export const SharedProposalSchema = z.object({
  proposalPath: z.string(),
  submitted: z.literal(false),
  diff: z.array(z.object({ canonicalKey: CanonicalKeySchema, before: RuleSchema.nullable(), after: RulePatchSchema })),
  affectedRuleCount: count,
  affectedAccountCount: count,
  warnings: z.array(z.string()),
  generatedAt: IsoDateTimeSchema,
});
export type SharedProposal = z.infer<typeof SharedProposalSchema>;

// ---------------------------------------------------------------------------------------------
// Publishing results
// ---------------------------------------------------------------------------------------------

export const PublishResultSchema = z.object({
  artifactId: ArtifactIdSchema,
  documentId: z.string(),
  url: z.string().url(),
  stageResults: ArtifactSchema.shape.stageResults,
  warnings: z.array(z.string()),
  publicationState: ArtifactSchema.shape.publicationState,
  revisionId: z.string().nullable(),
});
export type PublishResult = z.infer<typeof PublishResultSchema>;

export const SheetPublishResultSchema = z.object({
  artifact: ArtifactSchema,
  stageResults: ArtifactSchema.shape.stageResults,
  warnings: z.array(z.string()),
  mergeSummary: z.object({ added: count, updated: count, preservedHuman: count, retired: count }).nullable(),
  tableMode: z.enum(['native_table', 'range_fallback']).nullable(),
});
export type SheetPublishResult = z.infer<typeof SheetPublishResultSchema>;

// ---------------------------------------------------------------------------------------------
// Outreach
// ---------------------------------------------------------------------------------------------

export const EmailTemplateViewSchema = z.object({
  id: TemplateIdSchema,
  name: z.string(),
  category: TaskGroupCategorySchema.nullable(),
  subject: z.string(),
  body: z.string(),
  variables: z.array(z.string()),
  usesEmployeeList: z.boolean(),
  version: z.number().int().positive(),
  contentVersion: SemVerSchema.nullable(),
});
export type EmailTemplateView = z.infer<typeof EmailTemplateViewSchema>;

export const SenderInfoSchema = z.object({
  email: z.string(),
  displayName: z.string().nullable(),
  sendAs: z.string().nullable(),
  /** Administrator-configured internal To/summary recipient default. */
  internalRecipient: z.string().nullable(),
  /** The name rendered for {analyst_name}: the compose override, else the connected identity's first name. */
  analystName: z.string().nullable(),
});

// real-providers change: SendConfirmationSchema removed with `outreach.send`; drafts are reviewed and sent from Gmail.

// ---------------------------------------------------------------------------------------------
// Notes / migration / diagnostics / retention
// ---------------------------------------------------------------------------------------------

export const NoteSaveResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('saved'), revision: count, savedAt: IsoDateTimeSchema }),
  z.object({ status: z.literal('conflict'), current: NoteSchema }),
]);
export type NoteSaveResult = z.infer<typeof NoteSaveResultSchema>;

export const RetentionInfoSchema = z.object({
  snapshotHistoryDays: z.literal(30),
  keepLastCompleteSnapshot: z.literal(true),
  incompleteStagingDays: z.literal(7),
  rumiConversationDays: z.literal(90),
  contextCacheDays: z.literal(30),
  localDiagnosticsDays: z.literal(30),
  notesAndOverrides: z.literal('until_deleted'),
  artifactLinks: z.literal('until_deleted'),
  exportedArtifacts: z.literal('destination_policy'),
  auditOutbox: z.literal('until_acknowledged'),
  backups: z.literal('not_uploaded'),
  lastCleanupAt: IsoDateTimeSchema.nullable(),
  nextCleanupAt: IsoDateTimeSchema.nullable(),
  cleanupPending: z.boolean(),
  staleAfterDays: z.literal(30),
});
export type RetentionInfo = z.infer<typeof RetentionInfoSchema>;

export const MigrationPlanSchema = z.object({
  folderPath: z.string(),
  sourceVersion: z.string().nullable(),
  detected: z.array(z.object({ kind: z.enum(['accounts', 'notes', 'overrides', 'agendas', 'schedule', 'settings', 'templates', 'snapshots']), count: count })),
  accounts: z.array(z.object({
    sourceId: z.string(),
    name: z.string(),
    provider: ProviderSchema.nullable(),
    colorPreference: z.string().nullable(),
    suggestedAccountId: AccountIdSchema.nullable(),
    candidates: z.array(z.object({ accountId: AccountIdSchema, displayName: z.string(), score: z.number() })),
  })),
  warnings: z.array(z.string()),
  requiredDiskBytes: count,
  freeDiskBytes: count.nullable(),
  canRun: z.boolean(),
  planDigest: z.string(),
});
export type MigrationPlan = z.infer<typeof MigrationPlanSchema>;

export const MigrationMappingSchema = z.object({
  sourceId: z.string(),
  /** null = skip this source account. */
  accountId: AccountIdSchema.nullable(),
  createNew: z.object({ displayName: z.string().min(1).max(120), provider: ProviderSchema.or(z.literal('template_only')) }).optional(),
});

export const MigrationResultSchema = z.object({
  imported: z.record(z.string(), count),
  skipped: z.record(z.string(), count),
  warnings: z.array(z.string()),
  backupPath: z.string(),
  startedAt: IsoDateTimeSchema,
  completedAt: IsoDateTimeSchema,
  status: z.enum(['completed', 'completed_with_warnings', 'failed', 'interrupted']),
});
export type MigrationResult = z.infer<typeof MigrationResultSchema>;

export const ApiLogEntrySchema = z.object({
  timestamp: IsoDateTimeSchema,
  provider: ProviderSchema,
  method: HttpMethodSchema,
  endpointTemplate: z.string(),
  httpStatus: z.number().int().nullable(),
  durationMs: count,
  pageOrdinal: count.nullable(),
  providerRequestId: z.string().nullable(),
  errorCode: SyncErrorCodeSchema.nullable(),
  retryAttempt: count,
  responseBytes: count.nullable(),
  datasetId: DatasetIdSchema.nullable(),
  rateLimitRemaining: count.nullable(),
  retryAfterMs: count.nullable(),
});
export type ApiLogEntry = z.infer<typeof ApiLogEntrySchema>;

export const DiagnosticEntrySchema = z.object({
  id: z.string(),
  timestamp: IsoDateTimeSchema,
  level: z.enum(['debug', 'info', 'warn', 'error']),
  source: z.enum(['main', 'worker', 'renderer']),
  category: z.string(),
  /** Scrubbed message; never bodies, tokens or addresses. */
  message: z.string(),
  operationId: OperationIdSchema.nullable(),
  accountId: AccountIdSchema.nullable(),
  runId: RunIdSchema.nullable(),
});
export type DiagnosticEntry = z.infer<typeof DiagnosticEntrySchema>;

export const AuditOutboxStatusSchema = z.object({
  pending: count,
  lastAckSequence: count.nullable(),
  nextSequence: count,
  lastDeliveryAt: IsoDateTimeSchema.nullable(),
  lastAttemptAt: IsoDateTimeSchema.nullable(),
  nextAttemptAt: IsoDateTimeSchema.nullable(),
  oldestPendingAt: IsoDateTimeSchema.nullable(),
  lagSeconds: count.nullable(),
  faulted: z.boolean(),
  faultReason: z.string().nullable(),
  rejectedTotal: count,
});
export type AuditOutboxStatus = z.infer<typeof AuditOutboxStatusSchema>;

export const RumiStatusSchema = z.object({
  assignmentReady: z.boolean(),
  brokerOnline: z.boolean(),
  model: z.string().nullable(),
  higherCapabilityModel: z.string().nullable(),
  maskedIdentifier: z.string().nullable(),
  policy: z.object({
    policyVersion: z.string(),
    store: z.literal(false),
    maxOutputTokens: z.number().int().positive().nullable(),
    budgetRemainingMicroUsd: MicroUsdSchema.nullable(),
    budgetLimitMicroUsd: MicroUsdSchema.nullable(),
    perRequestMaxReservationMicroUsd: MicroUsdSchema.nullable(),
    destinationBaseUrl: z.string().url().nullable(),
  }),
  disabledForAccount: z.boolean(),
  blockedReason: z.string().nullable(),
  activeOperationIds: z.array(OperationIdSchema),
});
export type RumiStatus = z.infer<typeof RumiStatusSchema>;

export const RumiUsageSummarySchema = z.object({
  requestCount: count,
  inputTokens: count,
  outputTokens: count,
  cachedTokens: count,
  reasoningTokens: count,
  estimatedCostMicroUsd: MicroUsdSchema,
  unknownUsageCount: count,
  byVerification: z.record(UsageVerificationStatusSchema, count),
  budget: z.object({ limitMicroUsd: MicroUsdSchema.nullable(), usedMicroUsd: MicroUsdSchema, remainingMicroUsd: MicroUsdSchema.nullable(), periodEnd: IsoDateTimeSchema.nullable() }),
  from: IsoDateTimeSchema.nullable(),
  to: IsoDateTimeSchema.nullable(),
});
export type RumiUsageSummary = z.infer<typeof RumiUsageSummarySchema>;

export const ContentDiffSchema = z.object({
  fromVersion: SemVerSchema.nullable(),
  toVersion: SemVerSchema,
  items: z.array(z.object({ type: TemplateTypeSchema, id: TemplateIdSchema, change: z.enum(['added', 'removed', 'changed', 'unchanged']), fromItemVersion: count.nullable(), toItemVersion: count.nullable(), hunks: z.array(DiffHunkSchema) })),
  manifest: ContentManifestSchema.nullable(),
});
export type ContentDiff = z.infer<typeof ContentDiffSchema>;

export const EmployeeReportFiltersSchema = z.object({
  employmentStates: z.array(EmploymentStateSchema).optional(),
  includeExcluded: z.boolean().default(false),
  failedOnly: z.boolean().default(false),
});

export const VulnerabilityCountsSchema = z.object({
  bySeverity: z.record(SeverityLevelSchema, count),
  totalFindings: count,
  totalPackages: count,
  totalAssets: count,
  excludedNonFixable: count,
  excludedUnknownFixability: count,
});

export const OutreachDuplicateWarningSchema = z.object({
  lastBatchId: OutreachBatchIdSchema,
  lastBatchAt: IsoDateTimeSchema,
  sameRecipientSet: z.boolean(),
  overlappingRecipientCount: count,
});

export const TaskGroupRefSchema = z.object({ accountId: AccountIdSchema, taskGroupId: TaskGroupIdSchema });
export const AgendaRefSchema = z.object({ agendaId: AgendaIdSchema });

export const AccountFrameworkSchema = z.object({
  id: z.string(),
  name: z.string(),
  provider: ProviderSchema,
  active: z.boolean(),
  excluded: z.boolean(),
});
export type AccountFramework = z.infer<typeof AccountFrameworkSchema>;

// ---------------------------------------------------------------------------------------------
// notifications change: local notification center (worker profile.db `notifications`)
// ---------------------------------------------------------------------------------------------

/** Newer notification with the same key replaces an older unread one (e.g. `sync:<accountId>`). */
export const NotificationDedupeKeySchema = z.string().min(1).max(200);

/** One stored notification: the api Notification plus local-only state. */
export const LocalNotificationSchema = NotificationSchema.extend({
  /** Set when the user dismissed it from the center; hidden unless `includeDismissed`. */
  dismissedAt: IsoDateTimeSchema.nullable(),
  /** Server notification id when `source` is `server` (unique locally); null when desktop-originated. */
  serverId: NotificationIdSchema.nullable(),
  dedupeKey: NotificationDedupeKeySchema.nullable(),
});
export type LocalNotification = z.infer<typeof LocalNotificationSchema>;

export const NotificationCountsSchema = z.object({
  unread: count,
  /** Unread notices with `required: true`; never muted, never dismissable while unread. */
  requiredUnread: count,
});
export type NotificationCounts = z.infer<typeof NotificationCountsSchema>;

/** Counts after a mutation plus how many rows it touched. */
export const NotificationMutationResultSchema = NotificationCountsSchema.extend({ affected: count });
export type NotificationMutationResult = z.infer<typeof NotificationMutationResultSchema>;

export const NOTIFICATION_DISMISS_REJECT_REASONS = ['required_unread'] as const;
export const NotificationDismissResultSchema = z.object({
  dismissed: z.array(NotificationIdSchema),
  rejected: z.array(z.object({ id: NotificationIdSchema, reason: z.enum(NOTIFICATION_DISMISS_REJECT_REASONS) })),
});
export type NotificationDismissResult = z.infer<typeof NotificationDismissResultSchema>;

export const NotificationPullResultSchema = z.object({
  /** Rows returned by GET /v1/notifications?updatedAfter=… */
  fetched: count,
  /** Rows inserted or updated locally (idempotent by server id). */
  merged: count,
  lastPolledAt: IsoDateTimeSchema,
});
export type NotificationPullResult = z.infer<typeof NotificationPullResultSchema>;

export const NOTIFICATIONS_CHANGED_REASONS = ['created', 'read', 'dismissed', 'cleared', 'server_merge', 'expired'] as const;
export const NotificationsChangedReasonSchema = z.enum(NOTIFICATIONS_CHANGED_REASONS);
export type NotificationsChangedReason = z.infer<typeof NotificationsChangedReasonSchema>;
